/**
 * Creates grid and axis cross-fade animations.
 *
 * Port of C++ NonMarksAnimationController from
 * tabanim/main/sceneanimation/NonMarksAnimationController.cpp
 *
 * The cross-fade effect uses 4 keyframes:
 * - Keyframe 0.0:  Source state (fully visible)
 * - Keyframe 0.49: Source middle state (colors transparent, spatial at midpoint)
 * - Keyframe 0.51: Dest middle state (colors transparent, spatial at midpoint)
 * - Keyframe 1.0:  Dest state (fully visible)
 *
 * This produces a smooth cross-fade: old fades out → quick switch → new fades in.
 */

import { AnimationsInParallel } from './AnimationsInParallel';
import { Animation } from './BaseAnimation';
import { DataStorePaneState } from './DataStorePaneState';
import { DataStoreValue, attributesNearlyEqual } from './DataStorePaneStateUtility';
import { DataStorePaths } from './DataStorePaths';
import { FractionWarperAnimation } from './FractionWarperAnimation';
import { FractionWarperTiming } from './FractionWarperTiming';
import { PaneStateAttribute } from './PaneStateAttribute';
import { PaneStateKeyframeAnimation } from './PaneStateKeyframeAnimation';
import { WarpingFunctionMapUtil, FractionWarperCurve } from './WarpingFunctionMapUtil';

/**
 * Axis direction enum matching C++ animation::WhichAxis
 */
export enum WhichAxis {
  X_AXIS,
  Y_AXIS,
}

/** Comparison epsilon for nearly-equal checks. C++ COMPARISON_EPSILON */
const COMPARISON_EPSILON = 1e-6;

// ============================================================================
// Attribute sets matching C++ NonMarksAnimationController static members
// ============================================================================

// C++ xGridRampAttributes = GridXLineColor | GridZeroPoint
const xGridRampAttributes: PaneStateAttribute[] = [PaneStateAttribute.GridXLineColor, PaneStateAttribute.GridZeroPoint];

// C++ yGridRampAttributes = GridYLineColor | GridZeroPoint
const yGridRampAttributes: PaneStateAttribute[] = [PaneStateAttribute.GridYLineColor, PaneStateAttribute.GridZeroPoint];

// C++ xAxisRampAttributes = XAxisDividerColor | AxisTickColor | GridSpacing | GridOrigin
const xAxisRampAttributes: PaneStateAttribute[] = [
  PaneStateAttribute.XAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
  PaneStateAttribute.GridSpacing,
  PaneStateAttribute.GridOrigin,
];

// C++ yAxisRampAttributes = YAxisDividerColor | AxisTickColor | GridSpacing | GridOrigin
const yAxisRampAttributes: PaneStateAttribute[] = [
  PaneStateAttribute.YAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
  PaneStateAttribute.GridSpacing,
  PaneStateAttribute.GridOrigin,
];

// C++ xAxisAttributes (full set for axis animation)
const xAxisAttributesWithGridSpacing: PaneStateAttribute[] = [
  PaneStateAttribute.XAxisMin,
  PaneStateAttribute.XAxisMax,
  PaneStateAttribute.XAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
  PaneStateAttribute.GridSpacing,
  PaneStateAttribute.GridOrigin,
];

const yAxisAttributesWithGridSpacing: PaneStateAttribute[] = [
  PaneStateAttribute.YAxisMin,
  PaneStateAttribute.YAxisMax,
  PaneStateAttribute.YAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
  PaneStateAttribute.GridSpacing,
  PaneStateAttribute.GridOrigin,
];

const xAxisAttributesNoGridSpacing: PaneStateAttribute[] = [
  PaneStateAttribute.XAxisMin,
  PaneStateAttribute.XAxisMax,
  PaneStateAttribute.XAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
];

const yAxisAttributesNoGridSpacing: PaneStateAttribute[] = [
  PaneStateAttribute.YAxisMin,
  PaneStateAttribute.YAxisMax,
  PaneStateAttribute.YAxisDividerColor,
  PaneStateAttribute.AxisTickColor,
];

// C++ xAxisSpatialAttributes = XAxisMin | XAxisMax
const xAxisSpatialAttributes: PaneStateAttribute[] = [PaneStateAttribute.XAxisMin, PaneStateAttribute.XAxisMax];

// C++ yAxisSpatialAttributes = YAxisMin | YAxisMax
const yAxisSpatialAttributes: PaneStateAttribute[] = [PaneStateAttribute.YAxisMin, PaneStateAttribute.YAxisMax];

// C++ xExtentsAttributes (pane bounds that affect grid)
const xExtentsAttributes: PaneStateAttribute[] = [
  PaneStateAttribute.PaneRectLeft,
  PaneStateAttribute.PaneRectRight,
  PaneStateAttribute.PaneRectDomainLeft,
  PaneStateAttribute.PaneRectDomainRight,
  PaneStateAttribute.PaneRectOriginX,
  PaneStateAttribute.XRangeMin,
  PaneStateAttribute.XRangeMax,
  PaneStateAttribute.MapDomainLeft,
  PaneStateAttribute.MapDomainRight,
];

const yExtentsAttributes: PaneStateAttribute[] = [
  PaneStateAttribute.PaneRectBottom,
  PaneStateAttribute.PaneRectTop,
  PaneStateAttribute.PaneRectDomainBottom,
  PaneStateAttribute.PaneRectDomainTop,
  PaneStateAttribute.PaneRectOriginY,
  PaneStateAttribute.YRangeMin,
  PaneStateAttribute.YRangeMax,
  PaneStateAttribute.MapDomainBottom,
  PaneStateAttribute.MapDomainTop,
];

// C++ xGridSpatialAttributes = GridZeroPoint
const xGridSpatialAttributes: PaneStateAttribute[] = [PaneStateAttribute.GridZeroPoint];
const yGridSpatialAttributes: PaneStateAttribute[] = [PaneStateAttribute.GridZeroPoint];

/**
 * Timing info for a grid/axis animation segment.
 * C++ Reference: MarkAnimationTiming
 */
interface AnimationTiming {
  delay: number; // fraction [0,1] when the animation starts
  duration: number; // fraction duration of the animation
}

export class NonMarksAnimationController {
  /**
   * Create the complete grid + axis animation group.
   * C++ Reference: NonMarksAnimationController::CreateGridAnimation()
   *
   * @param srcState - Source (FROM) pane state
   * @param destState - Destination (TO) pane state
   * @param outState - Shared output state to write interpolated values to
   * @returns Animation group (or null if nothing to animate)
   */
  createGridAnimation(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    outState: DataStorePaneState,
    nonMarksTiming?: { delay: number; duration: number } | null
  ): Animation | null {
    const gridTimings = this.getGridTransitionTimings(srcState, destState, nonMarksTiming);
    const result = new AnimationsInParallel();

    // Grid X animation
    if (gridTimings.x.delay > 0 || gridTimings.x.duration > 0) {
      const gridXAnim = this.createGridAxisAnimation(srcState, destState, outState, WhichAxis.X_AXIS, gridTimings.x, true);
      if (gridXAnim) {
        result.addChild(gridXAnim);
      }
    }

    // Grid Y animation
    if (gridTimings.y.delay > 0 || gridTimings.y.duration > 0) {
      const gridYAnim = this.createGridAxisAnimation(srcState, destState, outState, WhichAxis.Y_AXIS, gridTimings.y, true);
      if (gridYAnim) {
        result.addChild(gridYAnim);
      }
    }

    // Axis label animations
    this.addAxesAnimations(srcState, destState, outState, result, nonMarksTiming);

    if (result.getChildren().length > 0) {
      return result;
    }
    return null;
  }

  /**
   * Add axis label animations.
   * C++ Reference: NonMarksAnimationController::AddAxesAnimations()
   */
  private addAxesAnimations(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    outState: DataStorePaneState,
    result: AnimationsInParallel,
    nonMarksTiming?: { delay: number; duration: number } | null
  ): void {
    const axisTimings = this.getAxisLabelTransitionTimings(srcState, destState, nonMarksTiming);

    // X axis labels
    if (axisTimings.x.delay > 0 || axisTimings.x.duration > 0) {
      const xAxisAnim = this.createGridAxisAnimation(srcState, destState, outState, WhichAxis.X_AXIS, axisTimings.x, false);
      if (xAxisAnim) {
        result.addChild(xAxisAnim);
      }
    }

    // Y axis labels
    if (axisTimings.y.delay > 0 || axisTimings.y.duration > 0) {
      const yAxisAnim = this.createGridAxisAnimation(srcState, destState, outState, WhichAxis.Y_AXIS, axisTimings.y, false);
      if (yAxisAnim) {
        result.addChild(yAxisAnim);
      }
    }
  }

  /**
   * Create a single grid or axis cross-fade animation.
   * C++ Reference: NonMarksAnimationController::CreateGridAxisAnimation()
   *
   * @param srcState - Source state
   * @param destState - Dest state
   * @param outState - Output state
   * @param whichAxis - X or Y
   * @param timing - When to animate within the overall duration
   * @param isGridAnimation - true for grid, false for axis labels
   */
  private createGridAxisAnimation(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    outState: DataStorePaneState,
    whichAxis: WhichAxis,
    timing: AnimationTiming,
    isGridAnimation: boolean
  ): Animation | null {
    const minimumDuration = 0.01;
    if (timing.delay < minimumDuration && timing.duration === 0) {
      return null;
    }

    // Adjust timing if duration is 0 but delay > 0
    const adjustedTiming = { ...timing };
    if (adjustedTiming.duration === 0) {
      adjustedTiming.delay = adjustedTiming.delay - minimumDuration;
      adjustedTiming.duration = minimumDuration;
    }

    // Create fraction warper with timing
    const warper = new FractionWarperAnimation();
    const warperTiming = new FractionWarperTiming(
      null, // linear
      adjustedTiming.delay,
      adjustedTiming.delay + adjustedTiming.duration
    );
    warper.setTiming(warperTiming);

    if (isGridAnimation) {
      // Grid color animation
      const colorAttrs = whichAxis === WhichAxis.X_AXIS ? [PaneStateAttribute.GridXLineColor] : [PaneStateAttribute.GridYLineColor];
      const gridColorAnim = this.createCrossFadeAnimation(srcState, destState, outState, whichAxis, colorAttrs, isGridAnimation);
      warper.addChild(gridColorAnim);

      // Grid spatial animation
      const spatialAttrs = whichAxis === WhichAxis.X_AXIS ? xGridSpatialAttributes : yGridSpatialAttributes;
      const gridSpatialAnim = this.createCrossFadeAnimation(srcState, destState, outState, whichAxis, spatialAttrs, isGridAnimation);
      warper.addChild(gridSpatialAnim);
    } else {
      // Axis animation: determine if GridSpacing should be included
      // C++ checks if GridSpacing array lengths differ
      let xAxisAttrs = xAxisAttributesWithGridSpacing;
      let yAxisAttrs = yAxisAttributesWithGridSpacing;

      const spacingPath = DataStorePaths.getKeyPathForAttribute('', PaneStateAttribute.GridSpacing);
      if (spacingPath) {
        const srcSpacing = srcState.getValue(spacingPath);
        const destSpacing = destState.getValue(spacingPath);
        if (srcSpacing && destSpacing) {
          const srcArr = Array.isArray(srcSpacing) ? srcSpacing : [];
          const destArr = Array.isArray(destSpacing) ? destSpacing : [];
          if (srcArr.length !== destArr.length) {
            xAxisAttrs = xAxisAttributesNoGridSpacing;
            yAxisAttrs = yAxisAttributesNoGridSpacing;
          }
        }
      }

      const axisAttrs = whichAxis === WhichAxis.X_AXIS ? xAxisAttrs : yAxisAttrs;

      const axisAnim = this.createCrossFadeAnimation(srcState, destState, outState, whichAxis, axisAttrs, isGridAnimation);
      warper.addChild(axisAnim);
    }

    return warper;
  }

  /**
   * Create a 4-keyframe cross-fade animation for specific attributes.
   * C++ Reference: NonMarksAnimationController::CreateGridTransitionAnimation()
   */
  private createCrossFadeAnimation(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    outState: DataStorePaneState,
    whichAxis: WhichAxis,
    attrs: PaneStateAttribute[],
    isGridAnimation: boolean
  ): PaneStateKeyframeAnimation {
    const anim = new PaneStateKeyframeAnimation(outState, attrs);
    const linearWarp = WarpingFunctionMapUtil.getWarpingFunction(FractionWarperCurve.Linear) || null;

    // Create the 4 keyframe states
    const fourStates = this.createFourKeyframes(whichAxis, srcState, destState, isGridAnimation);

    // Keyframes: fade off src, switcheroo, fade on dest
    anim.addKeyframeAtFraction(0.0, fourStates[0], linearWarp);
    anim.addKeyframeAtFraction(0.49, fourStates[1], linearWarp);
    anim.addKeyframeAtFraction(0.51, fourStates[2], linearWarp);
    anim.addKeyframeAtFraction(1.0, fourStates[3], linearWarp);

    return anim;
  }

  /**
   * Create the 4 keyframe states for cross-fade.
   * C++ Reference: NonMarksAnimationController::CreateFourKeyframes()
   *
   * Returns [src, srcMiddle, destMiddle, dest]:
   * - src: original source state
   * - srcMiddle: source ramped to 50%, with colors made transparent
   * - destMiddle: dest ramped to 50%, with colors made transparent
   * - dest: original destination state
   */
  private createFourKeyframes(
    whichAxis: WhichAxis,
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    isGridAnimation: boolean
  ): [DataStorePaneState, DataStorePaneState, DataStorePaneState, DataStorePaneState] {
    const srcMiddleState = srcState.createCopy();
    const destMiddleState = destState.createCopy();

    if (isGridAnimation) {
      // Grid: ramp grid colors + zero point to midpoint
      const rampAttrs = whichAxis === WhichAxis.X_AXIS ? xGridRampAttributes : yGridRampAttributes;
      srcState.rampAttributesInto(destState, 0.5, rampAttrs, srcMiddleState);
      srcState.rampAttributesInto(destState, 0.5, rampAttrs, destMiddleState);

      // Set middle alphas to transparent
      const colorAttr = whichAxis === WhichAxis.X_AXIS ? PaneStateAttribute.GridXLineColor : PaneStateAttribute.GridYLineColor;
      this.setMiddleAlphasForAttribute(srcMiddleState, destMiddleState, colorAttr);
    } else {
      // Axis: ramp axis colors to midpoint
      const rampAttrs = whichAxis === WhichAxis.X_AXIS ? xAxisRampAttributes : yAxisRampAttributes;
      srcState.rampAttributesInto(destState, 0.5, rampAttrs, srcMiddleState);
      srcState.rampAttributesInto(destState, 0.5, rampAttrs, destMiddleState);

      // Set axis divider color to transparent at midpoint
      const dividerColorAttr = whichAxis === WhichAxis.X_AXIS ? PaneStateAttribute.XAxisDividerColor : PaneStateAttribute.YAxisDividerColor;
      this.setMiddleAlphasForAttribute(srcMiddleState, destMiddleState, dividerColorAttr);

      // Set axis tick color to transparent at midpoint
      const tickColorPath = DataStorePaths.getKeyPathForAttribute('', PaneStateAttribute.AxisTickColor);
      if (tickColorPath) {
        srcMiddleState.setColorTransparent(tickColorPath);
        destMiddleState.setColorTransparent(tickColorPath);
      }
    }

    return [srcState, srcMiddleState, destMiddleState, destState];
  }

  /**
   * Set the alpha of a color attribute to 0 in both middle states.
   * C++ Reference: NonMarksAnimationController::SetMiddleAlphasString()
   */
  private setMiddleAlphasForAttribute(
    srcMiddleState: DataStorePaneState,
    destMiddleState: DataStorePaneState,
    attribute: PaneStateAttribute
  ): void {
    const path = DataStorePaths.getKeyPathForAttribute('', attribute);
    if (!path) {
      return;
    }
    srcMiddleState.setColorTransparent(path);
    destMiddleState.setColorTransparent(path);
  }

  // =========================================================================
  // Timing computation
  // =========================================================================

  /**
   * Compute grid transition timings.
   * C++ Reference: NonMarksAnimationController::GetGridTransitionTimings()
   */
  private getGridTransitionTimings(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    nonMarksTiming?: { delay: number; duration: number } | null
  ): { x: AnimationTiming; y: AnimationTiming } {
    return this.getAxisTransitionTimings(srcState, destState, false, nonMarksTiming);
  }

  /**
   * Compute axis label transition timings.
   * C++ Reference: NonMarksAnimationController::GetAxisLabelTransitionTimings()
   */
  private getAxisLabelTransitionTimings(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    nonMarksTiming?: { delay: number; duration: number } | null
  ): { x: AnimationTiming; y: AnimationTiming } {
    return this.getAxisTransitionTimings(srcState, destState, true, nonMarksTiming);
  }

  /**
   * Compute transition timings for grid or axis labels.
   * C++ Reference: NonMarksAnimationController::GetAxisTransitionTimings()
   *
   * If the axis has spatial changes, the animation is timed to coincide with
   * mark position animation. Otherwise, timing is {0, 0} = no animation needed.
   */
  private getAxisTransitionTimings(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    considerLabelPlacement: boolean,
    nonMarksTiming?: { delay: number; duration: number } | null
  ): { x: AnimationTiming; y: AnimationTiming } {
    let xHasChanges = false;
    let yHasChanges = false;

    if (considerLabelPlacement) {
      // Axis label changes: check axis spatial attributes
      xHasChanges = this.axisHasSpatialChanges(srcState, destState, xAxisSpatialAttributes);
      yHasChanges = this.axisHasSpatialChanges(srcState, destState, yAxisSpatialAttributes);
    } else {
      // Grid changes: check pane extents + grid spatial attributes
      xHasChanges = this.gridHasSpatialChanges(srcState, destState, xExtentsAttributes, xGridSpatialAttributes);
      yHasChanges = this.gridHasSpatialChanges(srcState, destState, yExtentsAttributes, yGridSpatialAttributes);
    }

    // Use resolved nonMarks timing when provided; fall back to full-range [0, 1].
    // C++ ref: paneInfo.GetAnimationParams().GetNonMarksParams().GetOverallTiming()
    const activeTiming: AnimationTiming =
      nonMarksTiming != null && nonMarksTiming.duration > 0
        ? { delay: nonMarksTiming.delay, duration: nonMarksTiming.duration }
        : { delay: 0.0, duration: 1.0 };

    const xTiming: AnimationTiming = xHasChanges ? activeTiming : { delay: 0, duration: 0 };
    const yTiming: AnimationTiming = yHasChanges ? activeTiming : { delay: 0, duration: 0 };

    return { x: xTiming, y: yTiming };
  }

  /**
   * Check if axis spatial attributes (min/max) differ between states.
   * C++ Reference: NonMarksAnimationController::AxisHasSpatialChanges()
   */
  private axisHasSpatialChanges(srcState: DataStorePaneState, destState: DataStorePaneState, spatialAttrs: PaneStateAttribute[]): boolean {
    return !attributesNearlyEqual(
      srcState.getValue.bind(srcState) as (path: string) => DataStoreValue,
      destState.getValue.bind(destState) as (path: string) => DataStoreValue,
      spatialAttrs,
      COMPARISON_EPSILON
    );
  }

  /**
   * Check if grid has spatial changes (pane extents or grid positions differ).
   * C++ Reference: NonMarksAnimationController::GridHasSpatialChanges()
   */
  private gridHasSpatialChanges(
    srcState: DataStorePaneState,
    destState: DataStorePaneState,
    extentsAttrs: PaneStateAttribute[],
    gridAttrs: PaneStateAttribute[]
  ): boolean {
    if (
      !attributesNearlyEqual(
        srcState.getValue.bind(srcState) as (path: string) => DataStoreValue,
        destState.getValue.bind(destState) as (path: string) => DataStoreValue,
        extentsAttrs,
        COMPARISON_EPSILON
      )
    ) {
      return true;
    }
    if (
      !attributesNearlyEqual(
        srcState.getValue.bind(srcState) as (path: string) => DataStoreValue,
        destState.getValue.bind(destState) as (path: string) => DataStoreValue,
        gridAttrs,
        COMPARISON_EPSILON
      )
    ) {
      return true;
    }
    return false;
  }
}
